import { LoginUrls } from '@tableau/shared-urls';
import * as queryString from 'query-string';
import React from 'react';
import { DndProvider } from 'react-dnd';
import HTML5Backend from 'react-dnd-html5-backend';
import MultiBackend, { TouchTransition } from 'react-dnd-multi-backend';
import TouchBackend from 'react-dnd-touch-backend';
import { connect } from 'react-redux';
import { Redirect, Route, RouteComponentProps, Switch } from 'react-router-dom';

import { PKCERouter } from '../../../pkce/PKCERouter';
import { AuthLevel, getSessionManagementServiceInstance, SessionManagementService } from '../../../sessionManagement/SessionManagementService';
import { SessionUserSelectors } from '../../selectors/SessionInfoSelectors';
import { siteUrlsSelector } from '../../selectors/SiteUrlsSelectors';
import { AppState } from '../../state/AppState';
import { MapStateToProps } from '../../utils/ConnectUtils';
import { AuthenticateConnectionRouter } from '../Authenticate/AuthenticateConnectionRouter';
import { LoginErrorProvider } from '../Login/LoginErrorProvider';
import { LoginUrlsWrapper } from '../Login/LoginUrls';
import { RequestPasswordResetProvider } from '../Login/RequestPasswordResetProvider';
import { ResetPasswordProvider, ResetPasswordProviderProps } from '../Login/ResetPasswordProvider';
import { SignedOutProvider, SiteSamlSignedOutProvider } from '../Login/SignedOutProviders';
import { SignInProvider, SignInProviderProps } from '../Login/SignInProvider';
import { SitePickerProvider } from '../Login/SitePickerProvider';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { Main } from './Main';
import { ProcessAndRemoveSamlQueryWrapper } from './ProcessAndRemoveSamlQueryWrapper';
import { RemoveTrailingSlashWrapper } from './RemoveTrailingSlashWrapper';
import { RouterError } from './RouterError';

export interface ResetPasswordRouteParams {
  readonly token: string;
}
export interface ResetPasswordRouteComponentProps extends RouteComponentProps<ResetPasswordRouteParams> { }

export interface RootSpaceOwnProps { }

export interface RootSpaceStateProps {
  readonly urls: SpacesSiteUrls;
  readonly isSessionMultiSite: boolean;
}

export type RootSpaceProps = RootSpaceOwnProps & RootSpaceStateProps;

export class RootSpaceComponent extends React.Component<RootSpaceProps> {

  private server: SessionManagementService;
  public constructor(props: RootSpaceProps) {
    super(props);
    this.server = getSessionManagementServiceInstance();
  }

  public render() {
    return (
      <DndProvider backend={MultiBackend} options={HTML5toTouch}>
        <Switch>
          <Route path={'/authenticate'} render={() => <AuthenticateConnectionRouter />} />
          <Route path={'/pkce'} render={() => <PKCERouter />} />
          <Route path={LoginUrlsWrapper.getInstance().signinPath()} render={this.signinProvider()} />
          <Route path={LoginUrlsWrapper.getInstance().loginErrorPath()} render={() => <LoginErrorProvider {...this.props} />} />
          <Route path={LoginUrlsWrapper.getInstance().signedOutPath()} render={() => <SignedOutProvider />} />
          <Route path={LoginUrlsWrapper.getInstance().siteSamlSignedOutPath()} render={() => <SiteSamlSignedOutProvider />} />
          <Route exact path={LoginUrlsWrapper.getInstance().passwordResetWithTokenPath(':token')} render={this.resetPasswordProvider()} />
          <Route exact path={LoginUrlsWrapper.getInstance().passwordResetUserNameBasePath()} render={this.resetPasswordProvider()} />
          <Route exact path={LoginUrlsWrapper.getInstance().requestPasswordResetPath()} render={() => <RequestPasswordResetProvider />} />
          {this.protectedRouteForSitePicker()}
          {this.server.isUserAuthorized(AuthLevel.Authenticated) &&
            <Route render={(routeProps: RouteComponentProps) =>
              <ProcessAndRemoveSamlQueryWrapper>
                <RemoveTrailingSlashWrapper>
                  <Main
                    routeLocationPath={routeProps.location.pathname}
                    routeLocationSearch={routeProps.location.search}
                  />
                </RemoveTrailingSlashWrapper>
              </ProcessAndRemoveSamlQueryWrapper>
            } />
          }
          <Route render={this.error} />
        </Switch>
      </DndProvider>
    );
  }

  private resetPasswordProvider = (): (props: ResetPasswordRouteComponentProps) => JSX.Element => {
    return (props: ResetPasswordRouteComponentProps): JSX.Element => {
      const currentQueryValues = queryString.parse(props.location.search);
      const username = currentQueryValues[LoginUrls.usernameQueryKey()];
      const resetPasswordProviderProps: ResetPasswordProviderProps = {
        token: props.match.params.token,
        username
      };
      return <ResetPasswordProvider {...resetPasswordProviderProps} />;
    };
  }

  private signinProvider = (): (props: RouteComponentProps) => JSX.Element => {
    return (props: RouteComponentProps): JSX.Element => {
      const signinProps: SignInProviderProps = {
        routeLocationSearch: props.location.search,
      };

      return <SignInProvider {...signinProps} />;
    };
  }

  private protectedRouteForSitePicker() {
    return <Route exact={true} path={LoginUrlsWrapper.getInstance().sitePath()} render={(props: RouteComponentProps) => {
      if (this.server.isUserAuthorized(AuthLevel.Authenticated)) {
        return <SitePickerProvider routeLocationSearch={props.location.search} />;
      } else {
        return <Redirect to={LoginUrlsWrapper.getInstance().signinPath()} />;
      }
    }} />;
  }

  private error = (): JSX.Element => {
    if (this.server.inSession()) {
      return <RouterError errorType={'default'} urls={this.props.urls} />;
    } else {
      return <Redirect to={LoginUrlsWrapper.getInstance().signinPath()} />;
    }
  }
}

const HTML5toTouch = {
  backends: [
    {
      backend: HTML5Backend
    },
    {
      backend: TouchBackend,
      options: { enableMouseEvents: true, enableKeyboardEvents: true, delayTouchStart: 200 },
      transition: TouchTransition
    }
  ]
};

const mapStateToProps: MapStateToProps<RootSpaceStateProps> = (state: AppState) => ({
  urls: siteUrlsSelector(state),
  isSessionMultiSite: SessionUserSelectors.isSessionMultiSite(state),
});

export const ConnectedRootSpace: React.ComponentClass<RootSpaceOwnProps>
  = connect(mapStateToProps, null, null, { pure: false })(RootSpaceComponent);
